/**
 * Docling Service module
 * Handles communication with docling-serve API for document parsing
 */

import { logger } from '../core/logger';
import type GatewayService from '../core/gateway-service';
import {
  DOCLING,
  DOCUMENT_FORMATS,
  MIME_TYPE_TO_FORMAT,
} from '../core/app-constants';
import { DoclingTextExtractor } from './docling-text-extractor';

// Docling service options
export interface DoclingServiceOptions {
  gatewayService: GatewayService | null;
  useGateway?: boolean;
  timeout?: number;
}

// Docling parse result interface
export interface DoclingParseResult {
  text: string;
  json: any;
  markdown: string;
  html: string;
  doclingResult: any;
  metadata: DoclingMetadata;
  success: boolean;
  error?: string;
  warning?: string;
}

// Metadata interface
interface DoclingMetadata {
  parseTime: number;
  format: string;
  originalLength?: number;
  extractedLength?: number;
  extractionMethod?: string;
  status?: string;
  processingTime?: number;
  hasFormatting: boolean;
  hasTextContent?: boolean;
  availableFormats?: {
    json: boolean;
    markdown: boolean;
    html: boolean;
    text: boolean;
  };
  contentExtracted?: string;
}

// Available formats interface
interface AvailableFormats {
  json: boolean;
  markdown: boolean;
  html: boolean;
  text: boolean;
}

class DoclingService {
  private gatewayService: GatewayService | null;
  private timeout: number;
  private supportedFormats: string[];
  private textExtractor: DoclingTextExtractor;

  constructor(options: DoclingServiceOptions = { gatewayService: null }) {
    // Gateway service for document parsing (REQUIRED)
    this.gatewayService = options.gatewayService || null;

    if (!this.gatewayService) {
      throw new Error(
        'GatewayService is required for DoclingService. Please provide gatewayService in options.'
      );
    }

    this.timeout = options.timeout || DOCLING.DEFAULT_TIMEOUT_MS;

    // Build supported formats from centralized registry
    this.supportedFormats = this.buildSupportedFormatsList();

    // Initialize text extractor
    this.textExtractor = new DoclingTextExtractor();
  }

  /**
   * Build supported formats list from DOCUMENT_FORMATS registry
   * @returns {string[]} Array of supported MIME types and extensions
   */
  buildSupportedFormatsList(): string[] {
    const formats: string[] = [];
    for (const format of Object.values(DOCUMENT_FORMATS)) {
      if (format.supportsDocling) {
        formats.push(...format.mimeTypes);
        formats.push(...format.extensions);
      }
    }
    return formats;
  }

  /**
   * Parse document via Gateway API (routes to docling-serve)
   * @param {ArrayBuffer} arrayBuffer - Document file as ArrayBuffer
   * @param {string} filename - Original filename
   * @param {string} mimeType - MIME type of the document
   * @param {any} options - Parsing options (to_formats, language, etc.)
   * @returns {Promise<DoclingParseResult>} Parsed content with text and metadata
   */
  async parseDocument(
    arrayBuffer: ArrayBuffer,
    filename: string,
    mimeType: string,
    options: any = {}
  ): Promise<DoclingParseResult> {
    if (!this.gatewayService!.isInitialized()) {
      throw new Error(
        'Gateway service not initialized. Please initialize with authentication token first.'
      );
    }
    try {
      const startTime = performance.now();
      logger.debug('Parsing document via Gateway API...');

      // Submit document to gateway
      const taskId = await this.gatewayService!.parseDocuments(
        arrayBuffer,
        filename,
        mimeType,
        options
      );

      logger.debug(`Document submitted to Gateway, task ID: ${taskId}`);

      // Poll for completion (result unused, but await needed for side effects)
      await this.pollGatewayTaskStatus(taskId, startTime, options.language);

      // Fetch the actual result data
      logger.debug('Fetching result from Gateway for task:', taskId);
      const resultResponse = await this.gatewayService!.getParseResult(
        taskId,
        options.language || 'eng'
      );

      // Gateway returns {status: "ok", data: {...}}
      if (resultResponse.status !== 'ok' || !resultResponse.data) {
        throw new Error('Invalid response from Gateway: missing result data');
      }

      const result: any = resultResponse.data;
      const document = result.document || result;

      // Extract text and prepare response (same format as direct method)
      return this.prepareParseResponse(
        document,
        result,
        mimeType,
        arrayBuffer.byteLength,
        startTime
      );
    } catch (error) {
      logger.error('Document parsing error:', error);
      return {
        text: '',
        json: null,
        markdown: '',
        html: '',
        doclingResult: null,
        metadata: {
          parseTime: 0,
          format: this.getFormatFromMimeType(mimeType),
          hasFormatting: false,
        },
        success: false,
        error: (error as Error).message,
      };
    }
  }

  /**
   * Poll Gateway task status until completion
   * @param {string} taskId - Task ID to poll
   * @param {number} startTime - Start time for duration tracking
   * @param {string} language - Language code
   * @returns {Promise<any>} Completed task result
   */
  async pollGatewayTaskStatus(
    taskId: string,
    startTime: number,
    language: string = 'eng'
  ): Promise<any> {
    const pollInterval = DOCLING.POLL_INTERVAL_MS;
    const maxPolls = Math.floor(this.timeout / pollInterval);
    let pollCount = 0;

    while (pollCount < maxPolls) {
      pollCount++;

      // Wait before polling
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      try {
        const statusResponse = await this.gatewayService!.pollParseStatus(
          taskId,
          language
        );

        // Gateway returns {status: "ok", data: {task_status: "..."}}
        if (statusResponse.status !== 'ok' || !statusResponse.data) {
          logger.warn(
            `Poll ${pollCount}: Invalid status response`,
            statusResponse
          );
          continue;
        }

        const data: any = statusResponse.data;
        const taskStatus = data.task_status || data.status;
        logger.debug(`Poll ${pollCount}: Task status = ${taskStatus}`);

        // Check if task is complete
        if (
          taskStatus === 'SUCCESS' ||
          taskStatus === 'completed' ||
          taskStatus === 'success'
        ) {
          logger.info(
            `Document processed in ${(performance.now() - startTime).toFixed(0)}ms`
          );
          return statusResponse.data;
        }

        // Check if task failed (terminal state - don't retry)
        if (
          taskStatus === 'FAILURE' ||
          taskStatus === 'failed' ||
          taskStatus === 'error'
        ) {
          const errorMsg = data.error || data.message || 'Task failed';
          throw new Error(`Document processing failed: ${errorMsg}`);
        }

        // Task is still processing
      } catch (error) {
        // Re-throw immediately if it's a processing failure (not a network error)
        if (
          (error as Error).message &&
          (error as Error).message.includes('Document processing failed')
        ) {
          throw error;
        }
        // For network errors, only retry if we haven't hit max polls yet
        if (pollCount >= maxPolls) {
          throw error;
        }
        logger.warn(`Poll ${pollCount} error:`, (error as Error).message);
      }
    }

    throw new Error(
      `Timeout: Document processing did not complete within ${this.timeout}ms`
    );
  }

  /**
   * Prepare parse response from document data
   * @param {any} document - Document data from parsing result
   * @param {any} result - Full result object
   * @param {string} mimeType - MIME type of original document
   * @param {number} originalLength - Original file size in bytes
   * @param {number} startTime - Parse start time
   * @returns {DoclingParseResult} Formatted parse response
   */
  prepareParseResponse(
    document: any,
    result: any,
    mimeType: string,
    originalLength: number,
    startTime: number
  ): DoclingParseResult {
    this.logDocumentFields(document);

    // Extract plain text for the local parse
    const textContent =
      this.textExtractor.extractTextFromDoclingResult(document);
    logger.debug('Extracted text length:', textContent?.length || 0);

    // Check if document has no extractable text (e.g., image-only PDF)
    if (!textContent || textContent.trim().length === 0) {
      return this.createNoTextResponse(
        document,
        result,
        mimeType,
        originalLength,
        startTime
      );
    }

    // Document has text content - return success response
    return this.createSuccessResponse(
      document,
      result,
      mimeType,
      originalLength,
      startTime,
      textContent
    );
  }

  /**
   * Log available document fields for debugging
   * @param {any} document - Document object
   */
  logDocumentFields(document: any): void {
    logger.debug('Document fields available:', {
      hasJsonContent: !!document.json_content,
      hasTextContent: !!document.text_content,
      hasMdContent: !!document.md_content,
      hasHtmlContent: !!document.html_content,
      jsonContentType: document.json_content
        ? typeof document.json_content
        : 'N/A',
    });
  }

  /**
   * Create response for documents with no extractable text
   * @param {any} document - Document object
   * @param {any} result - Result object
   * @param {string} mimeType - MIME type
   * @param {number} originalLength - Original file size
   * @param {number} startTime - Parse start time
   * @returns {DoclingParseResult} No-text response object
   */
  createNoTextResponse(
    document: any,
    result: any,
    mimeType: string,
    originalLength: number,
    startTime: number
  ): DoclingParseResult {
    logger.warn('Document has no extractable text (possibly image-only)');

    const metadata = this.createMetadata(
      result,
      mimeType,
      originalLength,
      startTime,
      0,
      false
    );
    metadata.contentExtracted = 'no-text-content';

    return {
      text: '',
      json: document.json_content,
      markdown: document.md_content,
      html: document.html_content,
      doclingResult: result,
      metadata,
      success: true,
      warning:
        'Document contains no extractable text. It may contain only images or be empty.',
    };
  }

  /**
   * Create success response for documents with text
   * @param {any} document - Document object
   * @param {any} result - Result object
   * @param {string} mimeType - MIME type
   * @param {number} originalLength - Original file size
   * @param {number} startTime - Parse start time
   * @param {string} textContent - Extracted text content
   * @returns {DoclingParseResult} Success response object
   */
  createSuccessResponse(
    document: any,
    result: any,
    mimeType: string,
    originalLength: number,
    startTime: number,
    textContent: string
  ): DoclingParseResult {
    const metadata = this.createMetadata(
      result,
      mimeType,
      originalLength,
      startTime,
      textContent.length,
      true
    );
    metadata.contentExtracted = 'full-document-with-formatting';

    return {
      text: textContent,
      json: document.json_content,
      markdown: document.md_content,
      html: document.html_content,
      doclingResult: result,
      metadata,
      success: true,
    };
  }

  /**
   * Create metadata object for parse response
   * @param {any} result - Result object
   * @param {string} mimeType - MIME type
   * @param {number} originalLength - Original file size
   * @param {number} startTime - Parse start time
   * @param {number} extractedLength - Extracted text length
   * @param {boolean} hasTextContent - Whether document has text
   * @returns {DoclingMetadata} Metadata object
   */
  createMetadata(
    result: any,
    mimeType: string,
    originalLength: number,
    startTime: number,
    extractedLength: number,
    hasTextContent: boolean
  ): DoclingMetadata {
    return {
      parseTime: performance.now() - startTime,
      format: this.getFormatFromMimeType(mimeType),
      originalLength,
      extractedLength,
      extractionMethod: result.method || 'docling-serve',
      status: result.status || 'success',
      processingTime: result.processing_time,
      hasFormatting: hasTextContent,
      hasTextContent,
      availableFormats: this.getAvailableFormats(result),
    };
  }

  /**
   * Get available formats from result
   * @param {any} result - Result object with document data
   * @returns {AvailableFormats} Available formats object
   */
  getAvailableFormats(result: any): AvailableFormats {
    const doc = result.document || result;
    return {
      json: !!doc.json_content,
      markdown: !!doc.md_content,
      html: !!doc.html_content,
      text: !!doc.text_content,
    };
  }

  /**
   * Extract plain text from docling result (delegates to TextExtractor)
   * @param {any} document - Docling document object from API response
   * @returns {string} Extracted text content
   */
  extractTextFromDoclingResult(document: any): string {
    return this.textExtractor.extractTextFromDoclingResult(document);
  }

  /**
   * Convert markdown to plain text (delegates to TextExtractor)
   * @param {string} markdown - Markdown content
   * @returns {string} Plain text
   */
  markdownToPlainText(markdown: string): string {
    return this.textExtractor.markdownToPlainText(markdown);
  }

  /**
   * Extract text from JSON content structure (delegates to TextExtractor)
   * @param {any | string} jsonContent - Docling JSON content
   * @returns {string} Extracted text
   */
  extractTextFromJsonContent(jsonContent: any | string): string {
    return this.textExtractor.extractTextFromJsonContent(jsonContent);
  }

  /**
   * Extract text from a document element (delegates to TextExtractor)
   * @param {any | string} element - Document element
   * @returns {string} Extracted text
   */
  extractTextFromElement(element: any | string): string {
    return this.textExtractor.extractTextFromElement(element);
  }

  /**
   * Extract text from docling pages structure (delegates to TextExtractor)
   * @param {any[]} pages - Docling pages array
   * @returns {string} Extracted text
   */
  extractTextFromPages(pages: any[]): string {
    return this.textExtractor.extractTextFromPages(pages);
  }

  /**
   * Get format name from MIME type
   * @param {string} mimeType - MIME type
   * @returns {string} Format name
   */
  getFormatFromMimeType(mimeType: string): string {
    return MIME_TYPE_TO_FORMAT[mimeType] || 'unknown';
  }

  /**
   * Check if file format is supported
   * @param {string} mimeTypeOrExtension - MIME type or file extension
   * @param {string | null} filename - Optional filename to check extension as fallback
   * @returns {boolean} True if supported
   */
  isFormatSupported(
    mimeTypeOrExtension: string,
    filename: string | null = null
  ): boolean {
    // Check if MIME type is supported
    if (
      mimeTypeOrExtension &&
      this.supportedFormats.includes(mimeTypeOrExtension)
    ) {
      return true;
    }

    // If MIME type check failed and filename is provided, check extension
    if (filename) {
      const extension = filename.toLowerCase().match(/\.[^.]+$/);
      if (extension && this.supportedFormats.includes(extension[0])) {
        return true;
      }
    }

    return false;
  }
}

export default DoclingService;
